import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { Icon, Spinner } from 'components';
import ActionMenu from 'components/ActionMenu';
import { ButtonV3 } from 'components/ComponentV2';
import Confirmation from 'components/Confirmation';
import Modal from 'components/Modal';
import Pagination from 'components/Pagination';
import Tooltip from 'components/Tooltip';
import { Checkbox, TextArea } from 'components/form';

import ProgressBar from 'components/ComponentV2/ProgressBar';
import { useChatRoom } from 'hooks/useChatRoom';
import { useNotification } from 'hooks/useNotification';

import { ApplicationAttributes } from 'lib/models/application';
import { EventTouchpointable } from 'lib/models/event';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { parseResponse } from 'lib/utils/parser';

import { useCampaigns } from 'lib/contexts/campaigns-context';
import { TouchpointState } from 'lib/models/opportunity';
import EmptyList from '../EmptyList';
import GuestModal from '../GuestModal';
import NewCountBadge from '../NewCountBadge';
import RegistrationSettingModal from '../RegistrationSettingModal';
import { useAttendeesFilter } from '../useAttendeesFilter';
import { inviteGuestModalInstance } from '../InviteGuestModal';
import styles from './Guests.module.scss';

export enum ConfirmationScreen {
  NO_SCREEN = 0,
  CANCEL_TICKET = 1,
}

export enum GuestTabs {
  REGISTERED = 0,
  WAITING = 1,
  INVITED = 2,
}

type GuestProps = {
  isOverview?: boolean;
};

export const Guests: React.FC<GuestProps> = ({
  isOverview = false,
}: GuestProps) => {
  const router = useRouter();
  const { id, waitlist } = router.query;
  const { isManager } = useAuth();

  const [t] = useTranslation('event-dashboard');

  const [registrationSettingModalOpen, setRegistrationSettingModalOpen] =
    useState<boolean>(false);
  const [cancelTicketCandidateDetails, setCancelTicketCandidateDetails] =
    useState<{
      id: string;
      name: string;
      reason?: string;
    }>({
      id: '',
      name: '',
    });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isDeclineButtonLoading, setIsDeclineButtonLoading] = useState(false);
  const [confirmationScreenSelected, setConfirmationScreenSelected] =
    useState<ConfirmationScreen>(ConfirmationScreen.NO_SCREEN);

  const { setInvitePopup } = useCampaigns();
  const {
    touchpoint,
    mutateTouchpoint,
    applications,
    setIsWaitlist,
    onRejectApplications,
    onReleaseTickets,
    inviteToCommunity,
    isApplicationsLoading,
    isWaitlist,
    touchpointDashboard,
    applicationListTotalCount,
    pageIndex,
    setPageIndex,
    setPerPageCount,
    perPageCount,
    mutateApplications,
    onCSVExport,
    markApplicantAsSeen,
    invitedGuests,
    invitedGuestsTotalCount,
    mutateInvitedGuests,
    isInvitedGuestsLoading,
    isInvitedTab,
    setIsInvitedTab,
  } = useAttendeesFilter();
  const { unseen_waitlist_count, status_counter, waitlist_count } =
    touchpointDashboard || {};
  useEffect(() => {
    setPerPageCount(isOverview ? 10 : 20);
  }, [isOverview]);

  const { createGroupChat, createMessageChat } = useChatRoom();

  const [selectedGuestState, setSelectedGuestState] = useState(0);

  const notificationInstance = useNotification();
  const {
    cohost_application,
    touchpointable,
    id: touchpointId,
    title,
    state,
  } = touchpoint || {};
  const {
    enforce_waitlist_for_all,
    enforce_capacity,
    capacity,
    waitlist: hasCapacityWaitlist,
  } = parseResponse(touchpointable as EventTouchpointable) || {};
  const isHost = cohost_application ? false : true;

  useEffect(() => {
    if (id) {
      setIsWaitlist(waitlist === 'true');
      setSelectedGuestState(waitlist === 'true' ? 1 : 0);
    }
  }, [waitlist]);

  const onInviteToCommunity = async (candidate_ids: Array<string>) => {
    try {
      const response = await inviteToCommunity(candidate_ids);
      if (response)
        notificationInstance.success({
          title: t('guest-page.invite-to-community.sent.title'),
          message:
            candidate_ids.length === 1
              ? t('guest-page.invite-to-community.sent.message-single')
              : t('guest-page.invite-to-community.sent.message-multiple'),
        });
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  const [multiSelect, setMultiSelect] = useState<Array<string>>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const [guestListTabs, setGuestListTabs] = useState([
    {
      id: 0,
      value: t('guest-page.tabs.registered.value'),
      label: t('guest-page.tabs.registered.label'),
    },
    {
      id: 2,
      value: t('guest-page.tabs.invited.value'),
      label: t('guest-page.tabs.invited.label'),
    },
  ]);

  const onSelectAll = () => {
    if (selectAll) {
      setMultiSelect([]);
    } else {
      setMultiSelect(applications.map((item) => item.id));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (guestListTabs[selectedGuestState].id === GuestTabs.WAITING) {
      setIsWaitlist(true);
    } else {
      setIsWaitlist(false);
    }

    if (guestListTabs[selectedGuestState].id === GuestTabs.INVITED) {
      setIsInvitedTab(true);
      mutateInvitedGuests();
    } else {
      setIsInvitedTab(false);
    }
  }, [selectedGuestState]);

  useEffect(() => {
    if (applications.length && multiSelect.length === applications.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [multiSelect]);

  const getActionMenu = (
    id: string,
    isWaitlist: boolean,
    name: string,
    candidate_id: string
  ) => {
    if (!isWaitlist) {
      return [
        ...(isManager
          ? [
              {
                id: '1',
                label: t('guest-page.invite-to-community.title'),
                danger: false,
                onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                  await onInviteToCommunity([candidate_id]);
                },
              },
            ]
          : []),
        {
          id: '2',
          label: t('guest-page.cancel-ticket.title'),
          danger: true,
          onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setCancelTicketCandidateDetails({
              id,
              name,
            });
            setConfirmationScreenSelected(ConfirmationScreen.CANCEL_TICKET);
            setIsConfirmationModalOpen(true);
          },
        },
      ];
    } else {
      return [
        {
          id: '1',
          label: t('common.message'),
          danger: false,
          onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            createMessageChat(candidate_id);
          },
        },
        ...(isManager
          ? [
              {
                id: '2',
                label: t('guest-page.invite-to-community.title'),
                danger: false,
                onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                  await onInviteToCommunity([candidate_id]);
                },
              },
            ]
          : []),
        {
          id: '3',
          label: t('common.decline'),
          danger: true,
          onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            await onRejectApplications([id]);
          },
        },
      ];
    }
  };

  const [showGuestDetails, setShowGuestDetails] = useState(false);
  const [guestDetails, setGuestDetails] =
    useState<ApplicationAttributes | null>(null);

  const appliedGuestsList = useMemo(() => {
    return applications.map((item) => {
      const {
        id,
        candidate_id,
        applied_at,
        name,
        email,
        waitlist,
        reviewed_at,
      } = item;
      const isWaitlist = waitlist;

      return (
        <tr
          key={id}
          className={cn({
            [styles.notReviewed]: isWaitlist && !reviewed_at,
            [styles.selected]: multiSelect.includes(id),
          })}
          onClick={async () => {
            setGuestDetails(item);
            setShowGuestDetails(true);
            if (!reviewed_at) await markApplicantAsSeen([item.id]);
          }}
        >
          {isHost && isWaitlist && (
            <td>
              <Checkbox
                checked={multiSelect.includes(id)}
                hideLabel
                onClick={(e) => {
                  e?.stopPropagation();
                  if (multiSelect.includes(id)) {
                    setMultiSelect(multiSelect.filter((item) => item !== id));
                  } else {
                    setMultiSelect([...multiSelect, id]);
                  }
                }}
              />
            </td>
          )}

          <td>{name || email}</td>
          <td>{format(new Date(applied_at), 'MMM d, h:mma')}</td>
          <td className={styles.action}>
            {isWaitlist && isHost ? (
              <>
                <Tooltip
                  content={t('common.approve')}
                  position="top"
                  xAlign="center"
                  color="dark"
                >
                  <button
                    className={styles.approveButton}
                    onClick={async (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      await onReleaseTickets({
                        touchpointId,
                        touchpoint_application_ids: [id],
                      });
                    }}
                  >
                    <Icon iconName="icon_check" size="xsmallplus" />
                  </button>
                </Tooltip>
              </>
            ) : (
              <button
                className={styles.sendMessageButton}
                onClick={(event) => {
                  event.stopPropagation();
                  createMessageChat(candidate_id);
                }}
              >
                <Icon iconName="icon_send-message" size="xsmallplus" />
              </button>
            )}
            {isHost && (
              <ActionMenu
                options={getActionMenu(id, waitlist, name, candidate_id)}
                triggerButtonClassName={styles.actionMenu}
              />
            )}
          </td>
        </tr>
      );
    });
  }, [applications, multiSelect]);

  const invitedGuestList = useMemo(() => {
    return invitedGuests.map((item) => {
      const { id, created_at, first_name, last_name, email } = item;
      const name =
        first_name && last_name
          ? `${first_name} ${last_name}`
          : first_name || last_name;
      return (
        <tr key={id}>
          <td>{name || email}</td>
          <td>{format(new Date(created_at), 'MMM d, h:mma')}</td>
          <td className={styles.action} />
        </tr>
      );
    });
  }, [invitedGuests]);

  const guestData = useMemo(() => {
    const isLoading = isInvitedTab
      ? isInvitedGuestsLoading
      : isApplicationsLoading;

    const totalCount = isInvitedTab
      ? invitedGuestsTotalCount
      : applicationListTotalCount;

    const guests = isInvitedTab ? invitedGuestList : appliedGuestsList;

    return (
      <table>
        <thead>
          <tr>
            {isHost && isWaitlist && (
              <th>
                <Checkbox
                  checked={selectAll}
                  hideLabel
                  onClick={(e) => {
                    e?.stopPropagation();
                    onSelectAll();
                  }}
                />
              </th>
            )}
            <th className={styles.name}>{t('guest-page.table.head.guest')}</th>
            <th className={styles.date}>
              {isInvitedTab
                ? t('guest-page.table.head.invited')
                : t('guest-page.table.head.registered')}
            </th>
            <th className={styles.action}></th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={isHost && isWaitlist ? 4 : 3}
                className={styles.singleColumn}
              >
                <div className={styles.spinnerWrapper}>
                  <Spinner />
                </div>
              </td>
            </tr>
          ) : (
            guests
          )}
          {totalCount ? (
            <tr>
              <td
                colSpan={isHost && isWaitlist ? 4 : 3}
                className={styles.singleColumn}
              >
                <div className={styles.paginationWrapper}>
                  {isOverview ? (
                    <>
                      <div className={styles.showingText}>
                        {t('guest-page.table.pagination.showing-overview', {
                          count: totalCount <= 10 ? totalCount : 10,
                          total: totalCount,
                        })}
                        {totalCount > 10 && (
                          <span
                            className={styles.viewAllLink}
                            onClick={() => {
                              router.push(
                                router.pathname.replace('[id]', id as string) +
                                  '?tab=guests'
                              );
                            }}
                          >
                            {t('guest-page.view-all')}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.showingText}>
                        {t('guest-page.table.pagination.showing-guest', {
                          from: pageIndex * perPageCount + 1,
                          to:
                            totalCount >= (pageIndex + 1) * perPageCount
                              ? (pageIndex + 1) * perPageCount
                              : totalCount,
                          total: totalCount,
                        })}
                      </div>
                    </>
                  )}

                  {!isOverview && (
                    <Pagination
                      currentPage={pageIndex + 1}
                      onPageChange={(page: number) => setPageIndex(page - 1)}
                      pageSize={perPageCount}
                      totalCount={totalCount}
                    />
                  )}
                </div>
              </td>
            </tr>
          ) : (
            <></>
          )}
        </tbody>
      </table>
    );
  }, [appliedGuestsList, invitedGuestList, selectAll, isInvitedTab]);

  useEffect(() => {
    const hasWaitlistEnabled =
      enforce_waitlist_for_all || (enforce_capacity && hasCapacityWaitlist);
    const tabs = [
      {
        id: 0,
        value: t('guest-page.tabs.registered.value'),
        label: t('guest-page.tabs.registered.label'),
      },
    ];

    if (isHost && hasWaitlistEnabled) {
      tabs.push({
        id: 1,
        value: enforce_waitlist_for_all
          ? t('guest-page.tabs.awaiting-approval.value')
          : t('guest-page.tabs.waiting.value'),
        label: enforce_waitlist_for_all
          ? t('guest-page.tabs.awaiting-approval.label')
          : t('guest-page.tabs.waiting.label'),
      });
    }

    tabs.push({
      id: 2,
      value: t('guest-page.tabs.invited.value'),
      label: t('guest-page.tabs.invited.label'),
    });

    setGuestListTabs(tabs);
  }, [enforce_waitlist_for_all, isHost, enforce_capacity, hasCapacityWaitlist]);

  const cancelTicketConfirmationScreenDesc = touchpoint ? (
    <div className={styles.modalText}>
      <p className={styles.cancellation}>
        {t('guest-page.cancel-ticket.message')}
        <span> {cancelTicketCandidateDetails.name}</span>?
      </p>
      <TextArea
        rows={1}
        label={t('guest-page.cancel-ticket.label')}
        showOptionalLabel={true}
        placeholder={t('guest-page.cancel-ticket.placeholder')}
        onChange={(event) =>
          setCancelTicketCandidateDetails({
            ...cancelTicketCandidateDetails,
            reason: event.target.value,
          })
        }
        isValid={true}
        className={cn(styles.textarea, {})}
        textAreaClassName={styles.textareaBio}
        value={
          cancelTicketCandidateDetails.reason
            ? cancelTicketCandidateDetails.reason
            : ''
        }
      />
    </div>
  ) : null;

  const ConfirmationScreenProps = [
    {
      id: ConfirmationScreen.CANCEL_TICKET,
      title: t('guest-page.cancel-ticket.title'),
      desc: cancelTicketConfirmationScreenDesc,
      buttonContent: t('guest-page.cancel-ticket.button'),
      onCancel: () => {
        setIsConfirmationModalOpen(false);
        setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
      },
      onConfirm: async () => {
        if (cancelTicketCandidateDetails.id) {
          setIsDeclineButtonLoading(true);
          await onRejectApplications(
            [cancelTicketCandidateDetails.id],
            cancelTicketCandidateDetails.reason
          );
          mutateTouchpoint();
          setIsDeclineButtonLoading(false);
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
        }
      },
      isPrimary: false,
    },
  ];

  const getConfirmationModal = () => {
    const screen = ConfirmationScreenProps.find(
      (item) => item.id === confirmationScreenSelected
    );
    if (screen) {
      const { title, desc, buttonContent, onCancel, onConfirm, isPrimary } =
        screen;
      return (
        <Modal
          isOpen={isConfirmationModalOpen}
          onRequestClose={() => setIsConfirmationModalOpen(false)}
          isFullScreen={false}
        >
          <Confirmation
            title={title}
            desc={desc || ''}
            buttonContent={buttonContent}
            onCancel={onCancel}
            isLoading={isDeclineButtonLoading}
            onConfirm={onConfirm}
            isPrimary={isPrimary}
            hideSecondaryAction={true}
            secondaryActionOnRight={true}
          />
        </Modal>
      );
    }
  };

  const onMessageAllClick = async () => {
    if (id) {
      try {
        const room = await createGroupChat(id as string);
        if (room) {
          router.push(
            {
              pathname: PAGE_ROUTES.MESSAGES,
              query: { roomId: room.id },
            },
            PAGE_ROUTES.MESSAGES
          );
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const getCommonActionMenu = useMemo(() => {
    const menu = [
      {
        id: '1',
        label: t('guest-page.export-csv'),
        danger: false,
        onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          event.stopPropagation();
          await onCSVExport(title);
        },
      },
      ...(isWaitlist && id && multiSelect.length
        ? [
            {
              id: '2',
              label: t('guest-page.invite-to-community-multiple', {
                count: multiSelect.length,
              }),
              danger: false,
              onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                const candidateIds = applications
                  .filter((item) => multiSelect.includes(item.id))
                  .map((item) => item.candidate_id);
                await onInviteToCommunity(candidateIds);
              },
            },
            {
              id: '3',
              label: t('guest-page.approve-selected', {
                count: multiSelect.length,
              }),
              danger: false,
              onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                await onReleaseTickets({
                  touchpointId: id as string,
                  touchpoint_application_ids: multiSelect,
                });
                setMultiSelect([]);
              },
            },
            {
              id: '4',
              label: t('guest-page.decline-selected', {
                count: multiSelect.length,
              }),
              danger: true,
              onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                await onRejectApplications(multiSelect);
                setMultiSelect([]);
              },
            },
          ]
        : []),
    ];

    return menu;
  }, [isWaitlist, multiSelect, title]);
  const percentageCapacity =
    enforce_capacity && capacity
      ? ((status_counter?.applied || 0) / capacity) * 100
      : 100;

  const guestCount = useMemo(() => {
    return status_counter && waitlist_count !== undefined
      ? isHost
        ? status_counter?.applied || 0
        : (status_counter?.applied || 0) - waitlist_count
      : 0;
  }, [status_counter, waitlist_count]);
  return (
    <section className={styles.guests}>
      <div className={styles.guestsHeader}>
        <p>{t('guest-page.title')}</p>
      </div>
      {isOverview ? (
        <></>
      ) : (
        <div className={styles.statsWrapper}>
          <div className={styles.stats}>
            <div className={styles.guestCount}>
              {guestCount === 1
                ? t('guest-page.guest-count', {
                    count: guestCount,
                  })
                : t('guest-page.guests-count', {
                    count: guestCount,
                  })}
            </div>
            <ProgressBar
              percentCompleted={
                percentageCapacity <= 100 ? percentageCapacity : 100
              }
              color="Primary"
              height="12px"
              showProgressWhen0={false}
              className={styles.progressBar}
            />
            {enforce_capacity && capacity && (
              <div className={styles.descriptionText}>
                {t('guest-page.capacity-reached', {
                  percent:
                    percentageCapacity <= 100
                      ? Math.round(percentageCapacity)
                      : 100,
                })}
              </div>
            )}
          </div>
          <div className={styles.buttonWrapper}>
            {isManager && state === TouchpointState.PUBLISHED ? (
              <div
                className={styles.button}
                onClick={async () => {
                  setInvitePopup(true);
                  await inviteGuestModalInstance({ touchpoint });
                  mutateTouchpoint();
                }}
              >
                <div className={styles.buttonTitle}>
                  <Icon iconName={'icon_mail'} className={styles.icon} />
                  Invite guests
                </div>
                <div className={styles.buttonIcon}>
                  <Icon iconName="icon_arrow-right-2" />
                </div>
              </div>
            ) : null}
            {isHost && (
              <div
                className={styles.button}
                onClick={() => {
                  setRegistrationSettingModalOpen(true);
                }}
              >
                <div className={styles.buttonTitle}>
                  <Icon iconName={'icon_gear'} className={styles.icon} />
                  {t('guest-page.registration-settings')}
                </div>
                <div className={styles.buttonIcon}>
                  <Icon iconName="icon_arrow-right-2" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Tab.Group
        selectedIndex={selectedGuestState}
        onChange={setSelectedGuestState}
      >
        <div className={styles.tabListWrapper}>
          <Tab.List className={styles.tabList}>
            {guestListTabs.map((tab, index) => {
              return (
                <div key={tab.id}>
                  <Tab
                    className={({ selected }) =>
                      cn(styles.tab, { [styles.tabSelected]: selected })
                    }
                  >
                    {tab.label}{' '}
                    {unseen_waitlist_count &&
                    tab.label ===
                      t('guest-page.tabs.awaiting-approval.label') ? (
                      <NewCountBadge count={unseen_waitlist_count} />
                    ) : (
                      ''
                    )}
                  </Tab>
                  <span className={styles['divider-container']}>
                    <span
                      className={cn({
                        [styles['divider']]:
                          index !== selectedGuestState &&
                          index !== selectedGuestState - 1 &&
                          index !== guestListTabs.length - 1,
                      })}
                    ></span>
                  </span>
                </div>
              );
            })}
          </Tab.List>
          {applications.length ? (
            <div className={styles.actionWrapper}>
              {isOverview && applications.length > 10 && (
                <ButtonV3
                  onClick={() => {
                    router.push(
                      router.pathname.replace('[id]', id as string) +
                        '?tab=guests'
                    );
                  }}
                  size="small"
                >
                  {t('guest-page.view-all')}
                </ButtonV3>
              )}
              {isHost && (
                <ButtonV3
                  onClick={onMessageAllClick}
                  size="small"
                  color={isOverview ? 'secondary' : 'primary'}
                >
                  {t('common.message-all')}
                </ButtonV3>
              )}
              <ActionMenu
                options={getCommonActionMenu}
                triggerButtonClassName={styles.actionMenu}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
        {applications.length === 0 &&
        !isApplicationsLoading &&
        !isInvitedTab ? (
          <EmptyList
            title={t('guest-page.no-guests.title')}
            description={t('guest-page.no-guests.message')}
          />
        ) : (
          <>
            <Tab.Panels className={styles.tabPanels}>
              <Tab.Panel>{guestData}</Tab.Panel>
              {guestListTabs.length === 3 && (
                <Tab.Panel>
                  {!enforce_waitlist_for_all && !isApplicationsLoading ? (
                    <>
                      <div className={styles.waitlistInfoBox}>
                        <Icon iconName={'icon_info'} />
                        <p className={styles.waitlistInfoBoxText}>
                          {t('guest-page.ticket-released-message')}
                        </p>
                      </div>
                    </>
                  ) : (
                    <></>
                  )}
                  {guestData}
                </Tab.Panel>
              )}
              <Tab.Panel>
                {invitedGuestsTotalCount ? (
                  guestData
                ) : !isInvitedGuestsLoading ? (
                  <EmptyList
                    title={t('guest-page.no-invite-sent.title')}
                    description={t('guest-page.no-invite-sent.message')}
                    button={t('guest-page.no-invite-sent.button')}
                    onClick={async () => {
                      setInvitePopup(true);
                      await inviteGuestModalInstance({ touchpoint });
                      mutateTouchpoint();
                    }}
                  />
                ) : (
                  <div className={styles['spinner-invited-guest-table']}>
                    <Spinner />
                  </div>
                )}
              </Tab.Panel>
            </Tab.Panels>
          </>
        )}
      </Tab.Group>
      {isConfirmationModalOpen ? getConfirmationModal() : null}
      {multiSelect.length > 0 && (
        <div className={styles.showSelection}>
          <div className={styles.showSelectionText}>
            {t('guest-page.selected-count', { count: multiSelect.length })}
          </div>
          <ButtonV3
            onClick={async () => {
              await onReleaseTickets({
                touchpointId: id as string,
                touchpoint_application_ids: multiSelect,
              });
              setMultiSelect([]);
            }}
            color="secondary"
            startIcon="icon_check"
          >
            {t('common.approve')}
          </ButtonV3>
          <ButtonV3
            onClick={async () => {
              await onRejectApplications(multiSelect);
              setMultiSelect([]);
            }}
            color="secondary"
            startIcon="icon_close"
          >
            {t('common.decline')}
          </ButtonV3>
        </div>
      )}
      <RegistrationSettingModal
        isOpen={registrationSettingModalOpen}
        onClose={() => {
          setRegistrationSettingModalOpen(false);
          mutateTouchpoint();
        }}
      />
      <GuestModal
        isOpen={showGuestDetails}
        onClose={() => {
          setShowGuestDetails(false);
          mutateApplications();
        }}
        applicant={guestDetails}
        touchpoint={touchpoint}
      />
    </section>
  );
};
